import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export default function AboutPage() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen pt-20 md:pt-24"
    >
      {/* Hero */}
      <section className="relative py-20 bg-[#2C5530]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">Nuestra Historia</span>
              <h1 className="font-cabinet font-bold text-3xl sm:text-4xl lg:text-5xl mt-3 mb-6">
                Más de 35 Años Compartiendo los Sabores de Chiloé
              </h1>
              <p className="text-white/80 text-lg mb-6">
                En Gama Foods, cada producto cuenta la historia de nuestra tierra.
                Desde 1985, hemos mantenido viva la tradición culinaria chilota, seleccionando los
                mejores ingredientes locales y elaborándolos con técnicas artesanales transmitidas
                de generación en generación.
              </p>
              <p className="text-white/80 mb-8">
                Nuestro compromiso es llevar a tu mesa la autenticidad y calidad que caracteriza 
                a los productos de Chiloé, respetando las recetas tradicionales y el entorno 
                natural que nos rodea.
              </p>
              <Link
                to="/productos"
                className="inline-flex items-center gap-2 bg-white text-[#2C5530] font-medium px-6 py-3 rounded-full hover:bg-white/90 transition-colors"
              >
                Ver Productos
                <ArrowRight size={18} />
              </Link>
            </div>
            
            <div className="relative">
              <img
                src="/images/foto9.jpeg"
                alt="Gama Foods Chiloé"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#E2B35B] text-[#1A231C] p-6 rounded-xl shadow-xl">
                <strong className="font-cabinet text-4xl block">35+</strong>
                <span className="text-sm">años de experiencia</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '500+', label: 'Familias Satisfechas' },
              { value: '100%', label: 'Artesanal' },
              { value: '50+', label: 'Productos' },
              { value: '5', label: 'Zonas de Reparto' },
            ].map((stat, i) => (
              <div key={i}>
                <span className="font-cabinet font-bold text-4xl text-[#2C5530]">{stat.value}</span>
                <span className="block text-[#808E83] mt-1">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C]">Nuestros Valores</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                emoji: '🌿',
                title: 'Frescura',
                desc: 'Seleccionamos los mejores productos cada día para garantizar la máxima frescura en tu mesa.'
              },
              {
                emoji: '🤝',
                title: 'Compromiso',
                desc: 'Trabajamos con productores locales de Chiloé, apoyando a nuestra comunidad y economía regional.'
              },
              {
                emoji: '⭐',
                title: 'Calidad',
                desc: 'Más de 35 años de experiencia nos respaldan. Cada producto pasa por estrictos controles de calidad.'
              },
            ].map((value, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm">
                <span className="text-4xl block mb-4">{value.emoji}</span>
                <h3 className="font-cabinet font-semibold text-xl mb-3">{value.title}</h3>
                <p className="text-[#4A5D4E]">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#D96C4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-white mb-4">
            ¿Quieres conocernos mejor?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            Visita nuestra sala de venta en Chonchi o contáctanos por WhatsApp. 
            Estamos aquí para atenderte.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/sala-venta"
              className="inline-flex items-center gap-2 bg-white text-[#D96C4A] font-medium px-6 py-3 rounded-full hover:bg-white/90 transition-colors"
            >
              Ver Sala de Venta
            </Link>
            <a
              href="https://wa.me/56994798582?text=Hola!+Quiero+saber+más+sobre+Gama+Foods"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-6 py-3 rounded-full hover:bg-[#1EBE5D] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Contáctanos
            </a>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
